'use client';

import { AlertCircle, ArrowUp, CheckCircle2, Loader2, RefreshCw, Sparkles } from 'lucide-react';
import { useTranslations } from 'next-intl';
import * as React from 'react';
import { Alert, AlertDescription } from '@/components/ui/Alert';
import { Badge } from '@/components/ui/Badge';
import { Button } from '@/components/ui/Button';
import { ScrollArea } from '@/components/ui/ScrollArea';
import { CHAT_INPUT_MAX_HEIGHT_PX } from '@/lib/constants';
import { MarkdownContent } from '@/lib/markdown/MarkdownContent';
import { cn } from '@/lib/utils';
import { usePromptBuilderStore } from './promptBuilderStore';

/* ------------------------------------------------------------------ */
/*  Props                                                              */
/* ------------------------------------------------------------------ */

interface SuggestedLimits {
  maxNodes: number;
  maxDepth: number;
  maxChildren: number;
}

interface PromptBuilderChatProps {
  /** Called when the user clicks "Accept & Create Research". */
  onAccept: (prompt: string, profile: string, explanation: string, limits?: SuggestedLimits) => void;
  placeholder?: string | undefined;
}

/* ------------------------------------------------------------------ */
/*  Constants                                                          */
/* ------------------------------------------------------------------ */

const MIN_CHARS = 1;
const MAX_CHARS = 5000;

/* ------------------------------------------------------------------ */
/*  Strip question blocks from streaming content                       */
/* ------------------------------------------------------------------ */

/**
 * Strip question JSON blocks from streaming content to prevent raw JSON from
 * flashing in the chat. Handles:
 * - Complete: ```question\n{...}\n```
 * - Complete: ```json\n{"type":"single_choice"...}\n```
 * - Partial (no closing fence): ```question\n{...  (at end of string)
 */
function stripStreamingQuestionBlocks(text: string): string {
  // Strip complete fenced question blocks
  let cleaned = text.replace(/```question\s*\n[\s\S]*?\n```/g, '');
  // Strip complete ```json blocks that contain question JSON
  cleaned = cleaned.replace(
    /```json\s*\n\{"type"\s*:\s*"(?:single_choice|multi_select)"[\s\S]*?\n```/g,
    '',
  );
  // Strip partial (still-streaming) question blocks at end of string
  cleaned = cleaned.replace(/```question\s*\n[\s\S]*$/, '');
  cleaned = cleaned.replace(
    /```json\s*\n\{"type"\s*:\s*"(?:single_choice|multi_select)"[\s\S]*$/,
    '',
  );
  // Strip bare JSON question lines
  cleaned = cleaned.replace(
    /^{"type"\s*:\s*"(?:single_choice|multi_select)".*}$/gm,
    '',
  );
  // Collapse trailing whitespace
  return cleaned.replace(/\n{3,}/g, '\n\n').trimEnd();
}

/* ------------------------------------------------------------------ */
/*  Message bubble (local to this file)                                */
/* ------------------------------------------------------------------ */

interface BubbleProps {
  sender: 'user' | 'assistant';
  content: string;
  isStreaming?: boolean;
}

function MessageBubble({ sender, content, isStreaming }: BubbleProps) {
  const t = useTranslations('research');
  const isUser = sender === 'user';

  return (
    <div className={cn('flex flex-col gap-1', isUser ? 'items-end' : 'items-start')}>
      <span className="px-1 text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
        {isUser ? t('promptBuilderChat.you') : t('promptBuilderChat.assistant')}
      </span>

      {isUser ? (
        <div className="max-w-[80%] rounded-lg border border-accent/15 bg-accent/8 p-3">
          <p className="whitespace-pre-wrap text-sm text-foreground">{content}</p>
        </div>
      ) : (
        <div className="w-full">
          <div className="max-w-[65ch] text-sm text-foreground">
            <MarkdownContent content={content} className="text-sm leading-[1.75]" />
            {isStreaming && (
              <span className="ml-0.5 inline-block h-4 w-0.5 animate-pulse bg-foreground/60 align-text-bottom" />
            )}
          </div>
        </div>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------ */
/*  Quick-reply option parser                                          */
/* ------------------------------------------------------------------ */

interface QuickReplyOption {
  /** The number label, e.g. "1" */
  label: string;
  /** Short display text (the bolded part or first few words) */
  text: string;
}

/**
 * Extract numbered options (e.g. "1. **Market trends** — …") from markdown.
 * Returns an empty array if fewer than 2 options are found.
 */
function parseQuickReplies(markdown: string): QuickReplyOption[] {
  // Match lines like "1. **Text**" or "1. Text — description"
  const optionRe = /^(\d+)\.\s+\*{0,2}(.+?)\*{0,2}\s*(?:[—–]|$)/gm;
  const options: QuickReplyOption[] = [];
  let m: RegExpExecArray | null;
  while ((m = optionRe.exec(markdown)) !== null) {
    options.push({ label: m[1] ?? '', text: (m[2] ?? '').trim() });
  }
  return options.length >= 2 ? options : [];
}

/* ------------------------------------------------------------------ */
/*  Main component                                                     */
/* ------------------------------------------------------------------ */

function PromptBuilderChat({ onAccept, placeholder }: PromptBuilderChatProps) {
  const t = useTranslations('research');
  /* --- Store selectors ------------------------------------------- */
  const storeState = usePromptBuilderStore((s) => s.state);
  const messages = usePromptBuilderStore((s) => s.messages);
  const currentStreamingMessage = usePromptBuilderStore((s) => s.currentStreamingMessage);
  const isStreaming = usePromptBuilderStore((s) => s.isStreaming);
  const turnCount = usePromptBuilderStore((s) => s.turnCount);
  const maxTurns = usePromptBuilderStore((s) => s.maxTurns);
  const suggestedPrompt = usePromptBuilderStore((s) => s.suggestedPrompt);
  const suggestedProfile = usePromptBuilderStore((s) => s.suggestedProfile);
  const profileExplanation = usePromptBuilderStore((s) => s.profileExplanation);
  const suggestedLimits = usePromptBuilderStore((s) => s.suggestedLimits);
  const structuredQuestions = usePromptBuilderStore((s) => s.structuredQuestions);
  const suggestedSections = usePromptBuilderStore((s) => s.suggestedSections);
  const suggestedExclusions = usePromptBuilderStore((s) => s.suggestedExclusions);
  const suggestionVersion = usePromptBuilderStore((s) => s.suggestionVersion);

  /* --- Local state ----------------------------------------------- */
  const [content, setContent] = React.useState('');
  const [multiSelectChoices, setMultiSelectChoices] = React.useState<Set<string>>(new Set());
  const textareaRef = React.useRef<HTMLTextAreaElement>(null);
  const scrollRef = React.useRef<HTMLDivElement>(null);

  const charCount = content.length;
  const isValidLength = charCount >= MIN_CHARS && charCount <= MAX_CHARS;
  const isBusy = storeState === 'loading' || storeState === 'streaming';
  const canSend = isValidLength && !isBusy;
  const turnsExhausted = turnCount >= maxTurns;

  const activeQuestion = React.useMemo(() => {
    if (isStreaming || isBusy) return null;
    // Prefer structured questions from backend
    if (structuredQuestions.length > 0) return structuredQuestions[0];
    // Fallback: parse numbered options from last assistant message
    if (messages.length === 0) return null;
    const last = messages[messages.length - 1];
    if (!last || last.role !== 'assistant') return null;
    const parsed = parseQuickReplies(last.content);
    if (parsed.length > 0) {
      return {
        question: '',
        type: 'single_choice' as const,
        options: parsed.map(p => ({ label: p.label, value: p.text })),
      };
    }
    return null;
  }, [messages, isStreaming, isBusy, structuredQuestions]);

  /* --- Clear multi-select on new messages ------------------------- */
  React.useEffect(() => {
    setMultiSelectChoices(new Set());
  }, [messages.length]);

  /* --- Auto-scroll ----------------------------------------------- */
  // biome-ignore lint/correctness/useExhaustiveDependencies: scroll must trigger on content changes
  React.useEffect(() => {
    const el = scrollRef.current;
    if (el) {
      el.scrollTop = el.scrollHeight;
    }
  }, [messages.length, currentStreamingMessage]);

  /* --- Textarea auto-resize -------------------------------------- */
  const adjustHeight = React.useCallback(() => {
    const el = textareaRef.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, CHAT_INPUT_MAX_HEIGHT_PX)}px`;
  }, []);

  /* --- Handlers -------------------------------------------------- */
  const handleSend = React.useCallback(() => {
    const trimmed = content.trim();
    if (!trimmed || !canSend) return;
    usePromptBuilderStore.getState().actions.sendMessage(trimmed);
    setContent('');
    requestAnimationFrame(() => {
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
    });
  }, [content, canSend]);

  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        handleSend();
      }
    },
    [handleSend],
  );

  const handleChange = React.useCallback(
    (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      setContent(e.target.value);
      adjustHeight();
    },
    [adjustHeight],
  );

  const handleRetry = React.useCallback(() => {
    // Re-send the last user message
    const lastUserMsg = [...messages].reverse().find((m) => m.role === 'user');
    if (lastUserMsg) {
      usePromptBuilderStore.getState().actions.sendMessage(lastUserMsg.content);
    }
  }, [messages]);

  const handleAccept = React.useCallback(() => {
    if (suggestedPrompt) {
      onAccept(suggestedPrompt, suggestedProfile, profileExplanation, suggestedLimits ?? undefined);
    }
  }, [suggestedPrompt, suggestedProfile, profileExplanation, suggestedLimits, onAccept]);

  /* --- Render ---------------------------------------------------- */
  return (
    <div className="flex flex-1 flex-col overflow-hidden rounded-md border border-border bg-surface">
      {/* Header */}
      <div className="flex items-center justify-between border-b border-border px-5 py-3">
        <h3 className="font-serif text-base font-semibold text-foreground">{t('promptBuilderChat.title')}</h3>
        <Badge>
          {t('promptBuilderChat.turnCounter', { current: turnCount, max: maxTurns })}
        </Badge>
      </div>

      {/* Messages area */}
      <ScrollArea ref={scrollRef} className="flex-1 px-5 py-4">
        {messages.length === 0 && !isStreaming ? (
          <div className="flex flex-col items-center justify-center gap-3 py-12 text-muted-foreground">
            <Sparkles className="h-8 w-8 opacity-30" />
            <p className="max-w-sm text-center text-sm">
              {t('promptBuilderChat.emptyState')}
            </p>
          </div>
        ) : (
          <div className="flex flex-col gap-6">
            {messages.map((msg, idx) => (
              <MessageBubble
                key={`${msg.role}-${msg.timestamp}-${idx}`}
                sender={msg.role}
                content={msg.content}
              />
            ))}

            {/* Streaming bubble */}
            {isStreaming && currentStreamingMessage && (
              <MessageBubble
                sender="assistant"
                content={stripStreamingQuestionBlocks(currentStreamingMessage)}
                isStreaming
              />
            )}

            {/* Single-choice quick-reply buttons */}
            {activeQuestion?.type === 'single_choice' && (
              <div className="flex flex-wrap gap-2 pl-1">
                {activeQuestion.options.map((opt, idx) => (
                  <button
                    key={`sc-${opt.label}-${idx}`}
                    type="button"
                    onClick={() => {
                      usePromptBuilderStore.getState().actions.sendMessage(`${opt.label}. ${opt.value}`);
                    }}
                    className="inline-flex items-center gap-1.5 rounded-full border border-accent/25 bg-accent/5 px-3 py-1.5 text-xs font-medium text-foreground/80 transition-colors hover:border-accent/50 hover:bg-accent/10 hover:text-foreground"
                  >
                    <span className="font-semibold text-accent">{t('promptBuilderChat.optionLabel', { label: opt.label })}</span>
                    {opt.value}
                  </button>
                ))}
              </div>
            )}

            {/* Multi-select tag buttons */}
            {activeQuestion?.type === 'multi_select' && (
              <div className="flex flex-col gap-2 pl-1">
                <div className="flex flex-wrap gap-2">
                  {activeQuestion.options.map((opt, idx) => {
                    const isSelected = multiSelectChoices.has(opt.value);
                    return (
                      <button
                        key={`ms-${opt.label}-${idx}`}
                        type="button"
                        onClick={() => {
                          setMultiSelectChoices(prev => {
                            const next = new Set(prev);
                            if (next.has(opt.value)) {
                              next.delete(opt.value);
                            } else {
                              next.add(opt.value);
                            }
                            return next;
                          });
                        }}
                        className={cn(
                          'inline-flex items-center gap-1.5 rounded-full border px-3 py-1.5 text-xs font-medium transition-colors',
                          isSelected
                            ? 'border-accent bg-accent/15 text-foreground'
                            : 'border-accent/25 bg-accent/5 text-foreground/80 hover:border-accent/50 hover:bg-accent/10',
                        )}
                      >
                        <span className={cn(
                          'flex h-3.5 w-3.5 shrink-0 items-center justify-center rounded-sm border',
                          isSelected
                            ? 'border-accent bg-accent text-white'
                            : 'border-muted-foreground/40',
                        )}>
                          {isSelected && (
                            <svg className="h-2.5 w-2.5" viewBox="0 0 10 10" fill="none" stroke="currentColor" strokeWidth="2">
                              <path d="M2 5l2.5 2.5L8 3" />
                            </svg>
                          )}
                        </span>
                        <span className="font-semibold text-accent">{t('promptBuilderChat.optionLabel', { label: opt.label })}</span>
                        {opt.value}
                      </button>
                    );
                  })}
                </div>
                {multiSelectChoices.size > 0 && (
                  <button
                    type="button"
                    onClick={() => {
                      const selected = activeQuestion.options
                        .filter(o => multiSelectChoices.has(o.value))
                        .map(o => `${o.label}. ${o.value}`)
                        .join(', ');
                      usePromptBuilderStore.getState().actions.sendMessage(selected);
                      setMultiSelectChoices(new Set());
                    }}
                    className="inline-flex w-fit items-center gap-1.5 rounded-md bg-accent px-4 py-2 text-xs font-semibold text-white transition-colors hover:bg-accent/90"
                  >
                    {t('promptBuilderChat.sendSelection', { count: multiSelectChoices.size })}
                  </button>
                )}
              </div>
            )}

            {/* Loading indicator (before any streaming starts) */}
            {storeState === 'loading' && !currentStreamingMessage && (
              <div className="flex items-center gap-2 text-muted-foreground">
                <Loader2 className="h-4 w-4 animate-spin" />
                <span className="text-sm">{t('promptBuilderChat.thinking')}</span>
              </div>
            )}
          </div>
        )}

        {/* Error state */}
        {storeState === 'error' && (
          <Alert variant="error" className="mt-4">
            <AlertDescription className="flex items-center justify-between">
              <span className="flex items-center gap-2">
                <AlertCircle className="h-4 w-4 shrink-0" />
                {turnsExhausted
                  ? t('promptBuilderChat.maxTurnsError')
                  : t('promptBuilderChat.genericError')}
              </span>
              {!turnsExhausted && (
                <Button variant="ghost" size="sm" onClick={handleRetry}>
                  <RefreshCw className="mr-1.5 h-3.5 w-3.5" />
                  {t('promptBuilderChat.retry')}
                </Button>
              )}
            </AlertDescription>
          </Alert>
        )}

        {storeState === 'critical_failure' && (
          <Alert variant="error" className="mt-4">
            <AlertDescription className="flex items-center gap-2">
              <AlertCircle className="h-4 w-4 shrink-0" />{t('promptBuilderChat.criticalError')}
            </AlertDescription>
          </Alert>
        )}

        {/* Suggested prompt card */}
        {suggestedPrompt && (
          <div
            key={`suggestion-v${suggestionVersion}`}
            className="mt-6 rounded-md border border-accent/30 bg-accent/5 p-4"
          >
            <div className="mb-2 flex items-center gap-2 text-sm font-medium text-foreground">
              <CheckCircle2 className="h-4 w-4 text-accent" />
              {t('promptBuilderChat.suggestedPrompt')}
            </div>
            <p className="whitespace-pre-wrap text-sm leading-relaxed text-foreground/90">
              {suggestedPrompt}
            </p>

            {suggestedProfile && (
              <div className="mt-3 border-t border-accent/15 pt-3">
                <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  {t('promptBuilderChat.recommendedProfile')}
                </span>
                <p className="mt-1 text-sm text-foreground/80">{suggestedProfile}</p>
              </div>
            )}

            {profileExplanation && (
              <div className="mt-2">
                <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  {t('promptBuilderChat.whyProfile')}
                </span>
                <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                  {profileExplanation}
                </p>
              </div>
            )}

            {suggestedLimits && (
              <div className="mt-2">
                <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  {t('promptBuilderChat.graphLimits')}
                </span>
                <div className="mt-1 flex gap-4 text-xs text-foreground/80">
                  <span>{t('promptBuilderChat.limitNodes', { count: suggestedLimits.maxNodes })}</span>
                  <span>{t('promptBuilderChat.limitDepth', { count: suggestedLimits.maxDepth })}</span>
                  <span>{t('promptBuilderChat.limitChildren', { count: suggestedLimits.maxChildren })}</span>
                </div>
              </div>
            )}

            {/* Sections */}
            {suggestedSections.length > 0 && (
              <div className="mt-3 border-t border-accent/15 pt-3">
                <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  {t('promptBuilderChat.reportSections')}
                </span>
                <div className="mt-2 flex flex-col gap-1.5">
                  {suggestedSections.map((sec, idx) => (
                    <div key={`sec-${idx}`} className="text-sm">
                      <span className="font-medium text-foreground/90">{sec.title}</span>
                      {sec.content && (
                        <span className="text-foreground/60"> — {sec.content}</span>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Exclusions */}
            {suggestedExclusions.length > 0 && (
              <div className="mt-3 border-t border-accent/15 pt-3">
                <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                  {t('promptBuilderChat.excludedTopics')}
                </span>
                <div className="mt-2 flex flex-wrap gap-1.5">
                  {suggestedExclusions.map((excl, idx) => (
                    <span
                      key={`excl-${idx}`}
                      className="inline-flex items-center rounded-full bg-muted px-2.5 py-0.5 text-xs text-muted-foreground"
                    >
                      {excl}
                    </span>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-4 flex gap-2">
              <Button variant="primary" size="sm" onClick={handleAccept}>
                <CheckCircle2 className="mr-1.5 h-3.5 w-3.5" />
                {t('promptBuilderChat.acceptStart')}
              </Button>
            </div>
          </div>
        )}
      </ScrollArea>

      {/* Input bar */}
      <div className="sticky bottom-0 border-t border-border bg-surface">
        <div className="flex items-end gap-2 px-4 py-3" style={{ minHeight: 56 }}>
          <div className="flex min-w-0 flex-1 flex-col gap-1.5">
            <textarea
              ref={textareaRef}
              value={content}
              onChange={handleChange}
              onKeyDown={handleKeyDown}
              placeholder={
                turnsExhausted
                  ? t('promptBuilderChat.maxTurnsPlaceholder')
                  : placeholder ?? t('promptBuilderChat.inputPlaceholder')
              }
              disabled={isBusy || turnsExhausted}
              rows={1}
              className={cn(
                'w-full resize-none rounded-md border border-border bg-surface px-3 py-2 text-sm',
                'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:border-accent',
                'placeholder:text-muted-foreground',
                'disabled:cursor-not-allowed disabled:opacity-50',
              )}
              style={{ maxHeight: CHAT_INPUT_MAX_HEIGHT_PX }}
            />
            <div className="flex items-center justify-between px-1">
              <span
                className={cn(
                  'text-xs',
                  charCount > 0 && charCount < MIN_CHARS
                    ? 'text-warning'
                    : charCount > MAX_CHARS
                      ? 'text-destructive'
                      : 'text-muted-foreground',
                )}
              >
                {charCount}/{MAX_CHARS}
                {charCount > 0 && charCount < MIN_CHARS && t('promptBuilderChat.charMin', { min: MIN_CHARS })}
              </span>
            </div>
          </div>

          <Button
            variant="primary"
            size="sm"
            onClick={handleSend}
            disabled={!canSend}
            className="h-9 w-9 shrink-0 cursor-pointer rounded-full p-0"
            aria-label={t('promptBuilderChat.sendMessage')}
          >
            {isBusy ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <ArrowUp className="h-4 w-4" />
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}

export { PromptBuilderChat };
